import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Euro, FileText, Search, Calendar, AlertTriangle } from "lucide-react";
import { EstrattoContoModal } from "@/components/EstrattoContoModal";

export default function AdminEstrattoConto() {
  const { authAxios } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [detailClientId, setDetailClientId] = useState(null);
  const [detailClientName, setDetailClientName] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  const toggleEscluso = async (item) => {
    if (togglingId) return;
    const newVal = !item.escluso_estratto_conto;
    setTogglingId(item.client_id);
    // Optimistic update
    setSummary((prev) => prev && {
      ...prev,
      items: prev.items.map((x) =>
        x.client_id === item.client_id ? { ...x, escluso_estratto_conto: newVal } : x
      ),
    });
    try {
      await authAxios.put(`/clients/${item.client_id}/toggle-estratto-conto`, { escluso: newVal });
      toast.success(newVal
        ? `${item.ragione_sociale} escluso dai Giri`
        : `${item.ragione_sociale} re-incluso nei Giri`);
    } catch (err) {
      // Rollback on error
      setSummary((prev) => prev && {
        ...prev,
        items: prev.items.map((x) =>
          x.client_id === item.client_id ? { ...x, escluso_estratto_conto: !newVal } : x
        ),
      });
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setTogglingId(null);
    }
  };

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/estratto-conto/summary");
      setSummary(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Serve un file PDF");
      return;
    }
    setUploading(true);
    const tid = toast.loading("Avvio elaborazione...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const startResp = await authAxios.post("/estratto-conto/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000, // upload only, processing avviene in background
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("Risposta server non valida");

      // Poll job status every 2 seconds (no Cloudflare timeout)
      let attempts = 0;
      const maxAttempts = 600; // ~20 min hard cap
      let finalJob = null;
      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        attempts += 1;
        let job;
        try {
          const s = await authAxios.get(`/estratto-conto/upload-status/${jobId}`, { timeout: 30000 });
          job = s.data;
        } catch (pollErr) {
          // network blip: keep polling
          continue;
        }
        if (job.status === "processing") {
          const pc = job.progress_current || 0;
          const pt = job.progress_total || 0;
          if (pt > 0) {
            toast.loading(`Elaborazione PDF: pagina ${pc}/${pt}`, { id: tid });
          } else {
            toast.loading("Elaborazione PDF in corso...", { id: tid });
          }
          continue;
        }
        if (job.status === "done" || job.status === "failed") {
          finalJob = job;
          break;
        }
      }

      if (!finalJob) {
        toast.error("Timeout elaborazione (>20 min). Controlla i log.", { id: tid, duration: 8000 });
      } else if (finalJob.status === "failed") {
        toast.error(finalJob.error || "Elaborazione fallita", { id: tid, duration: 8000 });
      } else {
        const r = finalJob.result || {};
        toast.success(r.message || "Estratto conto importato", { id: tid, duration: 4000 });
        if (r.clients_not_matched > 0) {
          toast.warning(
            `${r.clients_not_matched} clienti del PDF non corrispondono al DB (codici: ${(r.not_matched_codes || []).slice(0, 5).join(", ")}${(r.not_matched_codes || []).length > 5 ? "..." : ""})`,
            { duration: 6000 }
          );
        }
        await loadSummary();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg = "Errore caricamento PDF";
      if (detail) {
        msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      } else if (status) {
        msg = `Errore ${status}: ${err.message || "richiesta fallita"}`;
      } else if (err.message) {
        msg = err.message;
      }
      toast.error(msg, { id: tid, duration: 7000 });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const fmtEur = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
  const fmtDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("it-IT", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const filtered = React.useMemo(() => {
    if (!summary?.items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return summary.items;
    return summary.items.filter(
      (i) =>
        (i.ragione_sociale || "").toLowerCase().includes(q) ||
        (i.codice_cli || "").toLowerCase().includes(q)
    );
  }, [summary, search]);

  const totalSaldo = React.useMemo(
    () => (summary?.items || []).reduce((a, b) => a + (b.totale_saldo || 0), 0),
    [summary]
  );

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 max-w-5xl mx-auto">
        <PageHeader title="Estratto Conto" />

        <h1 className="font-heading text-2xl font-bold text-white mb-1" data-testid="ec-title">
          Estratto Conto Clienti
        </h1>
        <p className="text-zinc-400 text-sm mb-4">
          Carica il PDF dell'estratto conto completo. Verrà suddiviso per cliente e associato tramite il codice.
        </p>

        {/* Upload box */}
        <label className="block mb-4 cursor-pointer">
          <input
            type="file"
            accept=".pdf"
            className="hidden"
            disabled={uploading}
            onChange={handleUpload}
            data-testid="upload-ec-input"
          />
          <div className="p-5 rounded-xl border-2 border-dashed border-white/10 hover:border-orange-500/50 hover:bg-orange-500/5 transition-colors flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
              <Upload className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium">
                {uploading ? "Analisi in corso..." : "Carica PDF estratto conto"}
              </p>
              <p className="text-zinc-500 text-xs">
                Accetta PDF completo con tutti i clienti. L'import sovrascrive quello precedente.
              </p>
            </div>
          </div>
        </label>

        {/* Totals */}
        {summary && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="p-3 rounded-lg bg-[#18181b] border border-white/10">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Clienti</p>
              <p className="text-white font-bold text-base" data-testid="ec-total-clients">
                {summary.total_clients}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#18181b] border border-white/10">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Saldo totale</p>
              <p className="text-orange-400 font-bold text-base font-mono" data-testid="ec-total-saldo">
                {fmtEur(totalSaldo)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#18181b] border border-white/10">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Aggiornato
              </p>
              <p className="text-zinc-300 font-medium text-xs" data-testid="ec-last-uploaded">
                {fmtDate(summary.last_uploaded_at)}
              </p>
            </div>
          </div>
        )}

        {/* Search */}
        {summary && summary.items.length > 0 && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Cerca cliente o codice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10 h-10"
              data-testid="ec-search"
            />
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500 mx-auto" />
          </div>
        ) : !summary || summary.items.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Nessun estratto conto caricato</p>
            <p className="text-zinc-600 text-xs mt-1">Usa il box sopra per caricare un PDF</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((i) => (
              <div
                key={i.codice_cli}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setDetailClientId(i.client_id);
                  setDetailClientName(i.ragione_sociale);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailClientId(i.client_id);
                    setDetailClientName(i.ragione_sociale);
                  }
                }}
                data-testid={`ec-row-${i.codice_cli}`}
                className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center gap-3 cursor-pointer ${
                  i.escluso_estratto_conto
                    ? "border-red-500/20 bg-red-500/5 hover:border-red-500/40 opacity-70"
                    : "border-white/5 bg-[#18181b] hover:border-orange-500/30 hover:bg-orange-500/5"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium truncate">{i.ragione_sociale}</p>
                    <span className="text-zinc-500 text-[10px] font-mono shrink-0">{i.codice_cli}</span>
                  </div>
                  <p className="text-zinc-400 text-[10px]">
                    {i.n_fatture} fattur{i.n_fatture === 1 ? "a" : "e"}
                  </p>
                </div>
                <div className="text-right shrink-0 mr-1">
                  <p className="text-orange-400 font-mono text-sm font-bold">{fmtEur(i.totale_saldo)}</p>
                  {(i.totale_acconto || 0) > 0 && (
                    <p className="text-emerald-400 text-[10px] font-mono">
                      acc. {fmtEur(i.totale_acconto)}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={togglingId === i.client_id}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEscluso(i);
                  }}
                  data-testid={`ec-row-toggle-${i.codice_cli}`}
                  title={i.escluso_estratto_conto
                    ? "Cliente escluso dai Giri — tocca per re-includere"
                    : "Cliente attivo nei Giri — tocca per escludere"}
                  className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors shrink-0 disabled:opacity-50 ${
                    i.escluso_estratto_conto
                      ? "border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25"
                      : "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                  }`}
                >
                  {i.escluso_estratto_conto ? "Escluso" : "Attivo"}
                </button>
              </div>
            ))}
            {filtered.length === 0 && search && (
              <p className="text-zinc-500 text-center text-sm py-6">Nessun cliente trovato</p>
            )}
          </div>
        )}
      </main>

      {detailClientId && (
        <EstrattoContoModal
          clientId={detailClientId}
          clientName={detailClientName}
          onClose={() => setDetailClientId(null)}
          onToggleChanged={loadSummary}
        />
      )}
    </div>
  );
}
